import React, { useState, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCheck } from 'react-icons/fi';
import { fetchPermissions, updatePermission } from '../../utils/sentimentApi';

const ROLES = ['admin', 'team_lead', 'support_agent', 'sales', 'marketing', 'finance'];
const ACCESS_LEVELS = ['full', 'read', 'none'];

const ACCESS_BADGE = {
  full:  { label: 'Full',  color: 'var(--sm-green)',   bg: 'rgba(34,197,94,.12)' },
  read:  { label: 'Read',  color: 'var(--sm-amber)',   bg: 'rgba(234,179,8,.12)' },
  none:  { label: 'None',  color: 'var(--sm-muted)',   bg: 'var(--sm-border)' },
};

const adminEmail = () => localStorage.getItem('sentiment_user_email') || 'admin@savic.com';

export default function SentimentRBAC({ showToast }) {
  const [perms, setPerms]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(null); // "role|resource" key while saving

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchPermissions();
      setPerms(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpdate = async (role, resource, access_level) => {
    const key = `${role}|${resource}`;
    setSaving(key);
    try {
      const updated = await updatePermission({ role, resource, access_level, updated_by: adminEmail() });
      setPerms(prev => prev.map(p =>
        p.role === role && p.resource === resource ? { ...p, access_level: updated.access_level } : p
      ));
      showToast?.(`${role} / ${resource} → ${access_level}`, 'success');
    } catch (e) {
      setError(e.message);
      showToast?.(e.message, 'error');
    } finally {
      setSaving(null);
    }
  };

  // Group by resource for table pivot
  const resources = [...new Set(perms.map(p => p.resource))].sort();
  const byRoleResource = {};
  perms.forEach(p => {
    byRoleResource[`${p.role}|${p.resource}`] = p.access_level;
  });

  return (
    <div>
      <div className="sm-section-header">
        <h2 className="sm-section-title">Role-Based Access Control</h2>
        <button className="sm-btn sm-btn-ghost" onClick={load} title="Refresh"><FiRefreshCw size={14} /></button>
      </div>

      {error && <div className="sm-alert-err"><FiAlertTriangle /> {error}</div>}

      {loading ? (
        <div className="sm-loading"><div className="sm-spinner sm-spinner-lg" /><span>Loading permissions…</span></div>
      ) : perms.length === 0 ? (
        <div className="sm-empty"><div className="sm-empty-icon">🔐</div><span>No permissions found.</span></div>
      ) : (
        <>
          <p style={{ fontSize: 12, color: 'var(--sm-muted)', marginBottom: 16 }}>
            Click any cell to cycle the access level for that role/resource pair.
          </p>
          <div className="sm-table-wrap">
            <table className="sm-table">
              <thead>
                <tr>
                  <th>Resource</th>
                  {ROLES.map(r => (
                    <th key={r} style={{ textAlign: 'center', textTransform: 'capitalize', fontSize: 11 }}>
                      {r.replace(/_/g, ' ')}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {resources.map((resource, ri) => (
                  <tr key={resource} style={{ '--row-delay': `${ri * 20}ms` }}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{resource.replace(/_/g, ' ')}</td>
                    {ROLES.map(role => {
                      const level = byRoleResource[`${role}|${resource}`];
                      if (level === undefined) return <td key={role} />;
                      const style = ACCESS_BADGE[level] || ACCESS_BADGE.none;
                      const key = `${role}|${resource}`;
                      const isSaving = saving === key;
                      const next = ACCESS_LEVELS[(ACCESS_LEVELS.indexOf(level) + 1) % ACCESS_LEVELS.length];
                      return (
                        <td key={role} style={{ textAlign: 'center' }}>
                          <button
                            title={`Change to ${next}`}
                            disabled={isSaving}
                            onClick={() => handleUpdate(role, resource, next)}
                            style={{
                              background: style.bg,
                              color: style.color,
                              border: 'none',
                              borderRadius: 6,
                              padding: '3px 10px',
                              fontSize: 11,
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              opacity: isSaving ? 0.5 : 1,
                            }}
                          >
                            {isSaving ? '…' : <><FiCheck size={9} /> {style.label}</>}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginTop: 14, fontSize: 12, color: 'var(--sm-muted)' }}>
            {Object.entries(ACCESS_BADGE).map(([k, v]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ background: v.bg, color: v.color, borderRadius: 4, padding: '1px 7px', fontWeight: 700 }}>{v.label}</span>
              </span>
            ))}
            <span>· Click a cell to cycle access level</span>
          </div>
        </>
      )}
    </div>
  );
}
